<template>
  <div class="ps-page">
    <header class="ps-header">
      <div class="container d-flex align-items-center" style="max-width: 900px;">
        <img src="/claimify_logo.png" alt="Claimify" class="ps-logo" />
        <router-link to="/" class="ml-auto small font-weight-bold text-white">Sign in</router-link>
      </div>
    </header>

    <section class="container py-4" style="max-width: 900px;">
      <h1 class="ps-title">Register your health facility</h1>
      <p class="text-muted mb-4">
        Run your own patients on Claimify: enrol them, record encounters, hold paid telemedicine consultations and buy
        supplies from vendors. You can set up your price list, staff and consultation hours straight away; patient work
        opens once our team approves your facility.
      </p>

      <form class="ps-panel" @submit.prevent="submit" novalidate>
        <h6 class="ps-section">Facility</h6>
        <div class="form-row">
          <div class="form-group col-md-8">
            <label for="ps-name" class="ps-label">Facility name *</label>
            <input id="ps-name" v-model="form.facility_name" class="form-control" required autocomplete="organization" />
          </div>
          <div class="form-group col-md-4">
            <label for="ps-level" class="ps-label">Level of care *</label>
            <select id="ps-level" v-model="form.care_level" class="form-control" required>
              <option v-for="l in careLevels" :key="l" :value="l">{{ l }}</option>
            </select>
          </div>
          <div class="form-group col-md-4">
            <label for="ps-state" class="ps-label">State *</label>
            <select id="ps-state" v-model="form.state" class="form-control" required @change="loadLgas">
              <option value="">Select state…</option>
              <option v-for="s in states" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div class="form-group col-md-4">
            <label for="ps-lga" class="ps-label">LGA *</label>
            <select id="ps-lga" v-model="form.localgovt" class="form-control" required :disabled="!lgas.length" @change="loadWards">
              <option value="">Select LGA…</option>
              <option v-for="l in lgas" :key="l.id" :value="l.id">{{ l.local_name }}</option>
            </select>
          </div>
          <div class="form-group col-md-4">
            <label for="ps-ward" class="ps-label">Ward</label>
            <select id="ps-ward" v-model="form.ward" class="form-control" :disabled="!wards.length">
              <option value="">Select ward…</option>
              <option v-for="w in wards" :key="w.id" :value="w.id">{{ w.ward_name }}</option>
            </select>
          </div>
          <div class="form-group col-12">
            <label for="ps-address" class="ps-label">Address *</label>
            <input id="ps-address" v-model="form.address" class="form-control" required autocomplete="street-address" />
          </div>
          <div class="form-group col-md-4">
            <label for="ps-code" class="ps-label">Facility code</label>
            <input id="ps-code" v-model="form.facility_code" class="form-control" />
          </div>
          <div class="form-group col-md-4">
            <label for="ps-licence" class="ps-label">Practice licence number</label>
            <input id="ps-licence" v-model="form.licence_number" class="form-control" />
          </div>
          <div class="form-group col-md-4">
            <label for="ps-doc" class="ps-label">Licence document <span class="text-muted small">(PDF/JPG/PNG, 5 MB)</span></label>
            <input id="ps-doc" type="file" accept=".pdf,.jpg,.jpeg,.png" class="form-control-file" @change="onFile" />
          </div>
        </div>

        <h6 class="ps-section mt-2">Your login (facility account)</h6>
        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="ps-first" class="ps-label">First name *</label>
            <input id="ps-first" v-model="form.firstname" class="form-control" required autocomplete="given-name" />
          </div>
          <div class="form-group col-md-6">
            <label for="ps-last" class="ps-label">Last name *</label>
            <input id="ps-last" v-model="form.lastname" class="form-control" required autocomplete="family-name" />
          </div>
          <div class="form-group col-md-6">
            <label for="ps-email" class="ps-label">Email *</label>
            <input id="ps-email" v-model="form.email" type="email" class="form-control" required autocomplete="email" />
          </div>
          <div class="form-group col-md-6">
            <label for="ps-phone" class="ps-label">Phone *</label>
            <input v-phone id="ps-phone" v-model="form.phone_number" class="form-control" required autocomplete="tel" />
          </div>
          <div class="form-group col-md-6">
            <label for="ps-pass" class="ps-label">Password * <span class="text-muted small">(8+ characters)</span></label>
            <input id="ps-pass" v-model="form.password" type="password" class="form-control" required autocomplete="new-password" />
          </div>
          <div class="form-group col-md-6">
            <label for="ps-pass2" class="ps-label">Confirm password *</label>
            <input id="ps-pass2" v-model="form.password_confirmation" type="password" class="form-control" required autocomplete="new-password" />
          </div>
        </div>

        <div v-if="errors.length" class="alert alert-danger small py-2">
          <div v-for="e in errors" :key="e">{{ e }}</div>
        </div>

        <button type="submit" class="btn ps-btn btn-block" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm mr-2" role="status"></span>
          Register facility
        </button>
        <p class="small text-muted text-center mt-3 mb-0">
          Already registered? <router-link to="/">Sign in</router-link> ·
          Selling supplies? <router-link to="/vendor/signup">Register as a vendor</router-link>
        </p>
      </form>
    </section>
  </div>
</template>

<script>
import { saveIndependent } from "@/service/independent";

export default {
  data() {
    return {
      careLevels: ["Primary", "Secondary", "Tertiary", "Telemedicine"],
      states: [],
      lgas: [],
      wards: [],
      file: null,
      saving: false,
      errors: [],
      form: {
        facility_name: "", care_level: "Primary", state: "", localgovt: "", ward: "", address: "", facility_code: "",
        licence_number: "", firstname: "", lastname: "", email: "", phone_number: "", password: "", password_confirmation: "",
      },
    };
  },
  created() {
    this.axios.get("/api/v1/auth/states").then((res) => {
      this.states = (res.data.data || []).slice().sort((a, b) => a.name.localeCompare(b.name));
    });
  },
  methods: {
    loadLgas() {
      this.form.localgovt = "";
      this.form.ward = "";
      this.lgas = [];
      this.wards = [];
      if (!this.form.state) return;
      this.axios.get(`/api/v1/auth/lga/${this.form.state}`).then((res) => {
        this.lgas = (res.data.data || []).slice().sort((a, b) => String(a.local_name).localeCompare(String(b.local_name)));
      });
    },
    loadWards() {
      this.form.ward = "";
      this.wards = [];
      if (!this.form.localgovt) return;
      this.axios.get(`/api/v1/getwards/${this.form.localgovt}`).then((res) => {
        this.wards = (res.data.data || []).slice().sort((a, b) => String(a.ward_name).localeCompare(String(b.ward_name)));
      });
    },
    onFile(e) {
      this.file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    },
    submit() {
      this.errors = [];
      if (this.form.password !== this.form.password_confirmation) {
        this.errors = ["The passwords do not match."];
        return;
      }
      const data = new FormData();
      Object.entries(this.form).forEach(([k, v]) => { if (v !== "" && v !== null) data.append(k, v); });
      if (this.file) data.append("licence_document", this.file);

      this.saving = true;
      this.axios
        .post("/api/v1/auth/provider/register", data)
        .then((res) => {
          // Same session keys as Login.vue; Pusher.vue routes a provider to its dashboard.
          localStorage.setItem("jwt", res.data.token);
          localStorage.setItem("user", JSON.stringify(res.data.user));
          localStorage.setItem("agency", JSON.stringify(res.data.agency));
          saveIndependent(res.data.independent);
          this.$router.push("/pusher");
        })
        .catch((err) => {
          const body = (err.response && err.response.data) || {};
          this.errors = body.errors ? Object.values(body.errors).flat() : [body.message || "Registration failed."];
        })
        .finally(() => (this.saving = false));
    },
  },
};
</script>

<style scoped>
.ps-page { background: #f1f5f9; min-height: 100vh; }
.ps-header { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); padding: 14px 0; }
.ps-logo { height: 40px; background: #fff; border-radius: 8px; padding: 4px 8px; }
.ps-title { font-size: 1.6rem; font-weight: 800; color: #0f172a; letter-spacing: -0.02em; }
.ps-panel { background: #fff; border-radius: 14px; padding: 24px; box-shadow: 0 1px 4px rgba(0, 0, 0, .05); }
.ps-section { font-size: .75rem; text-transform: uppercase; letter-spacing: .05em; color: #64748b; font-weight: 700; margin-bottom: 12px; }
.ps-label { font-weight: 600; font-size: .85rem; color: #374151; }
.ps-btn { background: linear-gradient(135deg, #1d4ed8, #2563eb); color: #fff; border-radius: 10px; font-weight: 700; padding: 11px; }
.ps-btn:hover { opacity: .92; color: #fff; }
</style>
